import { createContext, useContext, useState, ReactNode } from 'react';

export type UploadTarget = 'notes' | 'flashcards' | null;

interface UploadModalState {
  isPageSelectionModalOpen: boolean;
  isUploadModalOpen: boolean;
  draggedFiles: File[];
  imageUrls: string[];
  blobUrl: string;
  selectedImages: string[];
  abortController: AbortController | null;
  uploadTarget: UploadTarget;
}

interface UploadModalActions {
  setIsPageSelectionModalOpen: (isOpen: boolean) => void;
  setIsUploadModalOpen: (isOpen: boolean) => void;
  setDraggedFiles: (files: File[]) => void;
  setImageUrls: (images: string[]) => void;
  setBlobUrl: (url: string) => void;
  setSelectedImages: (images: string[]) => void;
  setAbortController: (controller: AbortController | null) => void;
  setUploadTarget: (target: UploadTarget) => void;
}

interface UploadModalContextType {
  state: UploadModalState;
  actions: UploadModalActions;
}

const UploadModalContext = createContext<UploadModalContextType | undefined>(undefined);

export const useUploadModal = () => {
  const context = useContext(UploadModalContext);
  if (context === undefined) {
    throw new Error('useUploadModal must be used within an UploadModalProvider');
  }
  return context;
};

export const UploadModalProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [state, setState] = useState<UploadModalState>({
    isPageSelectionModalOpen: false,
    isUploadModalOpen: false,
    draggedFiles: [],
    imageUrls: [],
    blobUrl: "",
    selectedImages: [],
    abortController: null,
    uploadTarget: null,
  });

  const actions: UploadModalActions = {
    setIsPageSelectionModalOpen: (isOpen) => setState(prev => ({ ...prev, isPageSelectionModalOpen: isOpen })),
    setIsUploadModalOpen: (isOpen) => setState(prev => ({ ...prev, isUploadModalOpen: isOpen })),
    setDraggedFiles: (files) => setState(prev => ({ ...prev, draggedFiles: files })),
    setImageUrls: (images) => setState(prev => ({ ...prev, imageUrls: images })),
    setBlobUrl: (url) => setState(prev => ({ ...prev, blobUrl: url })),
    setSelectedImages: (images) => setState(prev => ({ ...prev, selectedImages: images })),
    setAbortController: (controller) => setState(prev => ({ ...prev, abortController: controller })),
    setUploadTarget: (target) => setState(prev => ({ ...prev, uploadTarget: target })),
  };

  return (
    <UploadModalContext.Provider value={{ state, actions }}>
      {children}
    </UploadModalContext.Provider>
  );
};
